import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, FormField, Input, Multiselect, SpaceBetween, Textarea } from '@cloudscape-design/components';
import { apiFetch } from '../../auth/apiClient';
import { getRoleDisplayName } from '../../utils/roleDisplay';
import { useMessaging } from './MessagingContext';

const formatDateTime = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const withPrefix = (subject, prefix) => {
  const base = String(subject || '').trim();
  if (!base) return `${prefix} (No subject)`;
  return base.toLowerCase().startsWith(`${prefix.toLowerCase()} `) ? base : `${prefix} ${base}`;
};

const PinnedMessagePanel = () => {
  const {
    pinnedMessage,
    composeMode,
    startNewMessage,
    startReply,
    startReplyAll,
    startForward,
    cancelCompose,
    unpinMessage,
  } = useMessaging();

  const [subjectValue, setSubjectValue] = useState('');
  const [bodyValue, setBodyValue] = useState('');
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [staffStatus, setStaffStatus] = useState('pending');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const isComposing = !!composeMode;
  const isReplyMode = composeMode === 'reply' || composeMode === 'replyAll';
  const isForwardMode = composeMode === 'forward';

  const pinnedMeta = useMemo(() => {
    if (!pinnedMessage) return null;
    return {
      subject: pinnedMessage.subject || '(No subject)',
      body: pinnedMessage.body || pinnedMessage.preview || '',
      sender: pinnedMessage.sender || null,
      recipients: Array.isArray(pinnedMessage.recipients) ? pinnedMessage.recipients : [],
      participants: Array.isArray(pinnedMessage.participants) ? pinnedMessage.participants : [],
      ownerId: pinnedMessage.ownerStaffProfileId || pinnedMessage.owner_staff_profile_id || null,
      folder: pinnedMessage.folder || null,
      deletedFrom: pinnedMessage.deletedFrom || pinnedMessage.deleted_from || null,
      receivedAt: pinnedMessage.receivedAt || pinnedMessage.created_at || pinnedMessage.timestamp || null,
    };
  }, [pinnedMessage]);

  const formatRoleDescription = useCallback((profile) => {
    if (!profile) return '';
    const rawRole = profile.primaryRole || profile.primary_role || '';
    if (!rawRole) return '';
    const displayRole = getRoleDisplayName(rawRole);
    const shouldShowRegion = displayRole === 'ISET Coordinator' || displayRole === 'Regional Manager';
    const roleLabel = shouldShowRegion ? displayRole : rawRole;
    const regionCode = profile.regionCode || profile.region_code || null;
    if (shouldShowRegion && regionCode) {
      const trimmedRegion = String(regionCode).trim();
      if (trimmedRegion) {
        return `${roleLabel} (${trimmedRegion.toUpperCase()})`;
      }
    }
    return roleLabel;
  }, []);

  const profileToOption = useCallback((profile) => {
    if (!profile) return null;
    const id = profile.staffProfileId || profile.staff_profile_id || profile.id;
    if (!id) return null;
    const label = profile.displayName || profile.display_name || profile.email || `Staff #${id}`;
    const desc = formatRoleDescription(profile) || profile.primaryRole || profile.primary_role || '';
    const rawRole = profile.primaryRole || profile.primary_role || '';
    return {
      value: String(id),
      label,
      description: desc || undefined,
      filteringTags: [profile.email, desc, rawRole].filter(Boolean),
    };
  }, [formatRoleDescription]);

  const loadStaffOptions = useCallback(async (filteringText = '') => {
    setStaffStatus('loading');
    try {
      const params = new URLSearchParams();
      if (filteringText) params.set('q', filteringText);
      const resp = await apiFetch(`/api/me/staff-profiles?${params.toString()}`);
      if (!resp.ok) {
        throw new Error('Failed to load staff');
      }
      const json = await resp.json();
      const options = (json?.items || []).map(profileToOption).filter(Boolean);
      setRecipientOptions(options);
      setStaffStatus('finished');
    } catch (err) {
      setStaffStatus('error');
    }
  }, [profileToOption]);

  useEffect(() => {
    loadStaffOptions('');
  }, [loadStaffOptions]);

  useEffect(() => {
    if (!isComposing) return;
    setSendError(null);
    if (composeMode === 'new') {
      setSelectedRecipients([]);
      setSubjectValue('');
      setBodyValue('');
      return;
    }
    if ((composeMode === 'reply' || composeMode === 'replyAll') && pinnedMeta) {
      setSubjectValue(withPrefix(pinnedMeta.subject || '', 'Re:'));
      setBodyValue('');

      const folderHint = pinnedMeta.folder === 'deleted' ? pinnedMeta.deletedFrom : pinnedMeta.folder;
      const fromProfile = folderHint === 'sent'
        ? (pinnedMeta.recipients?.[0] || pinnedMeta.sender || null)
        : (pinnedMeta.sender || null);
      const participants = pinnedMeta.participants || [];
      const ownerId = Number(pinnedMeta.ownerId);

      if (composeMode === 'replyAll') {
        const targets = participants
          .filter(p => {
            const pid = Number(p.staffProfileId || p.staff_profile_id || p.id);
            return Number.isFinite(pid) && pid !== ownerId;
          })
          .map(profileToOption)
          .filter(Boolean);
        setSelectedRecipients(targets);
      } else if (composeMode === 'reply') {
        const target = profileToOption(fromProfile);
        setSelectedRecipients(target ? [target] : []);
      }
      return;
    }
    if (composeMode === 'forward' && pinnedMeta) {
      setSelectedRecipients([]);
      setSubjectValue(withPrefix(pinnedMeta.subject || '', 'Fwd:'));
      const senderName = pinnedMeta.sender?.displayName || pinnedMeta.sender?.email || 'Unknown sender';
      const forwardedParts = [
        '---------- Forwarded message ----------',
        `From: ${senderName}`,
        pinnedMeta.receivedAt ? `Date: ${formatDateTime(pinnedMeta.receivedAt)}` : null,
        `Subject: ${pinnedMeta.subject || '(No subject)'}`,
        '',
        pinnedMeta.body || '',
      ].filter(Boolean);
      setBodyValue(forwardedParts.join('\n'));
    }
  }, [composeMode, isComposing, pinnedMeta, profileToOption]);

  const handleSend = useCallback(async () => {
    if (!bodyValue.trim()) {
      setSendError('Message body is required.');
      return;
    }
    if (!selectedRecipients.length) {
      setSendError('Choose at least one recipient.');
      return;
    }
    setSendError(null);
    setSending(true);
    try {
      const payload = {
        subject: subjectValue,
        body: bodyValue,
        toStaffProfileIds: selectedRecipients.map(opt => Number(opt.value)),
      };
      if ((composeMode === 'reply' || composeMode === 'replyAll') && pinnedMessage?.threadId) {
        payload.threadId = pinnedMessage.threadId;
      }
      const resp = await apiFetch('/api/me/staff-messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to send message');
      }
      try { window.dispatchEvent(new CustomEvent('staff-messages:refresh')); } catch {}
      cancelCompose();
      unpinMessage();
    } catch (err) {
      setSendError(err?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  }, [bodyValue, cancelCompose, composeMode, pinnedMessage?.threadId, selectedRecipients, subjectValue, unpinMessage]);

  if (isComposing) {
    return (
      <SpaceBetween size="s">
        <Box variant="p">
          {isReplyMode
            ? 'Reply to the selected message.'
            : (isForwardMode ? 'Forward the selected message.' : 'Compose a new secure message.')}
        </Box>
        <form>
          <SpaceBetween size="s">
            <FormField
              label="To"
              errorText={sendError && !selectedRecipients.length ? sendError : undefined}
              description="Select one or more staff recipients."
            >
              <Multiselect
                filteringType="manual"
                statusType={staffStatus}
                onLoadItems={({ detail }) => loadStaffOptions(detail.filteringText || '')}
                selectedOptions={selectedRecipients}
                onChange={({ detail }) => setSelectedRecipients(detail.selectedOptions)}
                options={recipientOptions}
                placeholder="Choose recipients"
                selectedAriaLabel="Selected"
                tokenLimit={4}
                keepOpen={false}
              />
            </FormField>
            <FormField label="Subject">
              <Input value={subjectValue} onChange={({ detail }) => setSubjectValue(detail.value)} placeholder="Subject" spellcheck={true} />
            </FormField>
            <FormField label="Message">
              <Textarea
                value={bodyValue}
                onChange={({ detail }) => setBodyValue(detail.value)}
                placeholder="Type a message…"
                rows={6}
                spellcheck={true}
              />
            </FormField>
            {sendError && (
              <Box color="text-status-error" fontSize="body-s">
                {sendError}
              </Box>
            )}
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="primary" disabled={sending} onClick={handleSend}>
                Send
              </Button>
              <Button onClick={cancelCompose}>Cancel</Button>
            </SpaceBetween>
          </SpaceBetween>
        </form>
      </SpaceBetween>
    );
  }

  if (!pinnedMessage || !pinnedMeta) {
    return (
      <SpaceBetween size="s">
        <Box variant="p">Start a new secure message to staff.</Box>
        <Button variant="primary" onClick={startNewMessage}>New message</Button>
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="s">
      <Box fontWeight="bold">{pinnedMeta.subject}</Box>
      <Box variant="small" color="text-body-secondary">
        From {pinnedMeta.sender?.displayName || pinnedMeta.sender?.email || 'Unknown sender'}
        {pinnedMeta.receivedAt ? ` • ${formatDateTime(pinnedMeta.receivedAt)}` : ''}
      </Box>
      <Box variant="p">{pinnedMeta.body || '—'}</Box>
      <SpaceBetween size="xs" direction="horizontal">
        <Button variant="primary" onClick={startReply}>Reply</Button>
        <Button onClick={startReplyAll}>Reply all</Button>
        <Button onClick={startForward}>Forward</Button>
        <Button onClick={unpinMessage}>Unpin</Button>
      </SpaceBetween>
    </SpaceBetween>
  );
};

export default PinnedMessagePanel;
